// Safety Dashboard product analytics — the RDV-only part of the Usage dashboard.
//
// Four sections, all driven by the SAME filter bar as the rest of the page (the
// parent passes its queryString): Engagement, Feature usage, Modes & data
// sources, Filter usage.
//
// Visible only to holders of `view_user_analytics` (Admin group, RDV accounts).
// The server enforces that on every endpoint — and asserts the @rdvsystems.com
// domain again on top of the permission — so this component only shapes the UI.
//
// Two honesty rules the numbers follow, carried over from the usage dashboard:
//   * Session length is averaged over CLOSED sessions only; the count still open
//     is shown next to it, because an open session has no length.
//   * "Active" time is accumulated dwell, which stops while the tab is hidden or
//     the user is idle. It is NOT login-to-logout wall clock, and both are shown
//     so a dashboard left open overnight cannot read as engagement.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  AXIS_TICK, CAT_LANE, LEGEND_STYLE, SMALL_AXIS_TICK, SectionCard, Tile,
  TOOLTIP_CURSOR, TOOLTIP_STYLE, catChartHeight, fmtDateTime, fmtDuration,
  getJson, shortLabel,
} from './usageUi';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const fmtHours = (h) => (h === null || h === undefined ? '—' : `${Number(h).toFixed(1)} h`);

// Top-N choices for the per-user table. 0 means "all".
const TOP_N = [5, 10, 25, 50, 0];
const topNLabel = (n) => (n === 0 ? 'All' : `Top ${n}`);

// Rows per page in the session list. The server pages it, so these are the
// sizes it is asked for — not a slice of something already fetched whole.
const PAGE_SIZES = [25, 50, 100, 200];

/** A collapsible sub-section inside a SectionCard — the feature groups are long
 *  enough that having all nine open at once buries the one being looked at.
 *  Open state is remembered per group, like the SectionCards above. */
const CollapsibleGroup = ({ id, title, meta, defaultOpen = false, children }) => {
  const storageKey = `rsaUsage:group:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOpen : saved === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem(storageKey, o ? '0' : '1'); } catch { /* ok */ }
    return !o;
  });
  return (
    <div className="ua-group">
      <button type="button" className="ua-group-head" onClick={toggle} aria-expanded={open}>
        <span className={`usage-section-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="ua-group-title">{title}</span>
        {meta && <span className="usage-section-meta">{meta}</span>}
      </button>
      {open && <div className="ua-group-body">{children}</div>}
    </div>
  );
};

/** A sortable column header. `dir` is 'asc' | 'desc'. */
const SortTh = ({ label, sortKey, sort, onSort, numeric }) => {
  const active = sort.key === sortKey;
  return (
    <th className={`ua-sort-th${numeric ? ' ua-num' : ''}${active ? ' ua-sorted' : ''}`}>
      <button type="button"
        onClick={() => onSort({ key: sortKey, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}
        title={`Sort by ${label}`}>
        {label}
        <span className="ua-sort-mark">{active ? (sort.dir === 'desc' ? '▾' : '▴') : '⇅'}</span>
      </button>
    </th>
  );
};

/** Sort rows by a key, nulls always last whichever way the column points — an
 *  empty cell is missing information, not the smallest value. */
function sortRows(rows, { key, dir }) {
  const sign = dir === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x === y) return 0;
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y)) * sign;
  });
}

/** Activity density by weekday and hour — "when do they actually work in it". */
const WeekHourGrid = ({ cells }) => {
  const byKey = useMemo(() => {
    const m = new Map();
    (cells || []).forEach((c) => m.set(`${c.weekday}|${c.hour}`, c.events));
    return m;
  }, [cells]);
  const max = useMemo(
    () => Math.max(1, ...(cells || []).map((c) => c.events || 0)), [cells]);
  if (!cells || !cells.length) return <p className="usage-empty">No activity in this range.</p>;
  return (
    <div className="ua-grid" role="table" aria-label="Activity by weekday and hour">
      <div className="ua-grid-row ua-grid-head" role="row">
        <span className="ua-grid-corner" />
        {HOURS.map((h) => (
          <span key={h} className="ua-grid-hour" role="columnheader">
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
      </div>
      {WEEKDAYS.map((d) => (
        <div key={d} className="ua-grid-row" role="row">
          <span className="ua-grid-day" role="rowheader">{d}</span>
          {HOURS.map((h) => {
            const n = byKey.get(`${d}|${h}`) || 0;
            return (
              <span key={h} className="ua-grid-cell" role="cell"
                title={`${d} ${String(h).padStart(2, '0')}:00 — ${n} event${n === 1 ? '' : 's'}`}
                style={{ opacity: n ? 0.15 + 0.85 * (n / max) : 0.04 }} />
            );
          })}
        </div>
      ))}
    </div>
  );
};

const UserAnalytics = ({ queryString, canView }) => {
  const [summary, setSummary] = useState(null);
  const [features, setFeatures] = useState(null);
  const [modes, setModes] = useState(null);
  const [filterUsage, setFilterUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [openCombo, setOpenCombo] = useState(null);
  const [userTop, setUserTop] = useState(10);
  const [userCustom, setUserCustom] = useState('');
  const [userSort, setUserSort] = useState({ key: 'sessions', dir: 'desc' });
  const [traceSessions, setTraceSessions] = useState(null);
  const [sessTotal, setSessTotal] = useState(0);
  const [sessSize, setSessSize] = useState(50);
  const [sessPage, setSessPage] = useState(0);
  const [sessLoading, setSessLoading] = useState(false);
  const [openTrace, setOpenTrace] = useState(null);
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      const [s, f, m, fu] = await Promise.all([
        getJson(`/api/telemetry/summary?${queryString}`),
        getJson(`/api/telemetry/features?${queryString}`),
        getJson(`/api/telemetry/modes?${queryString}`),
        getJson(`/api/telemetry/filters?${queryString}`),
      ]);
      setSummary(s.summary || {});
      setFeatures(f.features || null);
      setModes(m.data || null);
      setFilterUsage(fu.data || null);
    } catch (e) {
      setError(e.message || 'Could not load the analytics');
    } finally {
      setLoading(false);
    }
  }, [canView, queryString]);

  useEffect(() => { load(); }, [load]);

  // The session list is paged on the server, so it loads on its own — turning
  // a page must not refetch the four aggregates above.
  const loadSessions = useCallback(async () => {
    if (!canView) return;
    setSessLoading(true);
    try {
      const sess = await getJson(
        `/api/telemetry/sessions?${queryString}&limit=${sessSize}&offset=${sessPage * sessSize}`);
      setTraceSessions(sess.sessions || []);
      setSessTotal(sess.total || 0);
    } catch (e) {
      setError(e.message || 'Could not load the session list');
    } finally {
      setSessLoading(false);
    }
  }, [canView, queryString, sessPage, sessSize]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Changing the filters or the page size puts you back on page one — page 7 of
  // the old list is not page 7 of the new one.
  useEffect(() => { setSessPage(0); }, [queryString, sessSize]);

  // Changing the filters invalidates whichever trace is open.
  useEffect(() => { setOpenTrace(null); setTrace(null); }, [queryString]);

  const openSessionTrace = useCallback(async (sessionId) => {
    if (openTrace === sessionId) { setOpenTrace(null); setTrace(null); return; }
    setOpenTrace(sessionId);
    setTrace(null);
    setTraceLoading(true);
    try {
      const data = await getJson(`/api/telemetry/sessions/${encodeURIComponent(sessionId)}`);
      setTrace(data);
    } catch (e) {
      setError(e.message || 'Could not load the session trace');
    } finally {
      setTraceLoading(false);
    }
  }, [openTrace]);

  const shownUsers = useMemo(() => {
    const sorted = sortRows(summary?.top_users, userSort);
    const custom = parseInt(userCustom, 10);
    const n = Number.isFinite(custom) && custom > 0 ? custom : userTop;
    return n > 0 ? sorted.slice(0, n) : sorted;
  }, [summary, userSort, userTop, userCustom]);

  const topFeatures = useMemo(() => {
    if (!features) return [];
    return features.groups
      .flatMap((g) => g.features)
      .filter((f) => f.events > 0)
      .sort((a, b) => b.events - a.events)
      .slice(0, 15)
      .map((f) => ({ ...f, name: f.label }));
  }, [features]);

  // After every hook: a conditional return before one would break the hook order.
  if (!canView) return null;

  const pageCount = Math.max(1, Math.ceil(sessTotal / sessSize));
  const firstRow = sessTotal ? sessPage * sessSize + 1 : 0;
  const lastRow = Math.min(sessTotal, sessPage * sessSize + (traceSessions?.length || 0));

  return (
    <>
      {error && (
        <div className="usage-error" role="alert">
          User analytics unavailable: {error}
        </div>
      )}

      {/* ---------------- Engagement ---------------- */}
      <SectionCard id="ua-engagement" title="Safety Dashboard engagement"
        meta={loading ? 'loading…' : summary
          ? `${summary.sessions} session${summary.sessions === 1 ? '' : 's'} · ${summary.users} user${summary.users === 1 ? '' : 's'}`
          : undefined}>
        {!summary ? <p className="usage-empty">No data yet.</p> : (
          <>
            <div className="usage-tiles">
              <Tile label="Sessions" value={summary.sessions}
                hint={summary.open_sessions ? `${summary.open_sessions} still open` : 'all closed'} />
              <Tile label="Users" value={summary.users}
                hint={summary.sessions_per_user ? `${summary.sessions_per_user} sessions each` : undefined} />
              <Tile label="Average session"
                value={fmtDuration(summary.avg_session_s)}
                hint={`over ${summary.closed_sessions || 0} closed session${summary.closed_sessions === 1 ? '' : 's'}`} />
              <Tile label="Median session" value={fmtDuration(summary.median_session_s)} />
              <Tile label="Average active time"
                value={fmtDuration(summary.avg_active_s)}
                hint="paused while the tab is hidden or idle" />
              <Tile label="Total active time" value={fmtHours((summary.total_active_s || 0) / 3600)}
                hint={`of ${fmtHours((summary.total_session_s || 0) / 3600)} signed in`} />
              <Tile label="Recorded actions" value={(summary.events || 0).toLocaleString()} />
            </div>

            <h3>Sessions and active time per day</h3>
            {summary.daily?.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={summary.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={SMALL_AXIS_TICK} />
                  <YAxis yAxisId="left" tick={AXIS_TICK} />
                  <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Line yAxisId="left" type="monotone" dataKey="sessions" name="Sessions"
                    stroke="#38bdf8" strokeWidth={2} dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="users" name="Users"
                    stroke="#a78bfa" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="active_h" name="Active hours"
                    stroke="#34d399" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="usage-empty">No sessions in this range.</p>}

            <h3>When the dashboard is used</h3>
            <WeekHourGrid cells={summary.weekday_hour} />

            <h3>
              Per user
              <span className="ua-table-tools">
                {TOP_N.map((n) => (
                  <button key={n} type="button"
                    className={`ua-topn${userTop === n && !userCustom ? ' on' : ''}`}
                    onClick={() => { setUserTop(n); setUserCustom(''); }}>
                    {topNLabel(n)}
                  </button>
                ))}
                <input type="number" min="1" className="ua-topn-custom" placeholder="Custom"
                  value={userCustom}
                  onChange={(e) => setUserCustom(e.target.value.replace(/[^0-9]/g, ''))}
                  aria-label="Show this many users" />
              </span>
            </h3>
            {shownUsers.length ? (
              <>
                <div className="usage-table-wrap">
                  <table className="usage-table">
                    <thead>
                      <tr>
                        <SortTh label="User" sortKey="user_email" sort={userSort} onSort={setUserSort} />
                        <SortTh label="Customer" sortKey="customer" sort={userSort} onSort={setUserSort} />
                        <SortTh label="Sessions" sortKey="sessions" sort={userSort} onSort={setUserSort} numeric />
                        <SortTh label="Active time" sortKey="active_h" sort={userSort} onSort={setUserSort} numeric />
                        <SortTh label="Signed in" sortKey="total_h" sort={userSort} onSort={setUserSort} numeric />
                        <SortTh label="Last login" sortKey="last_login" sort={userSort} onSort={setUserSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {shownUsers.map((u) => (
                        <tr key={u.user_email}>
                          <td>{u.user_email}</td>
                          <td>{u.customer || '—'}</td>
                          <td className="ua-num">{u.sessions}</td>
                          <td className="ua-num">{fmtHours(u.active_h)}</td>
                          <td className="ua-num">{fmtHours(u.total_h)}</td>
                          <td>{fmtDateTime(u.last_login)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shownUsers.length < (summary.top_users || []).length && (
                  <p className="usage-hint">
                    Showing {shownUsers.length} of {summary.top_users.length} users.
                  </p>
                )}
              </>
            ) : <p className="usage-empty">No users in this range.</p>}
          </>
        )}
      </SectionCard>

      {/* ---------------- Feature usage ---------------- */}
      <SectionCard id="ua-features" title="Feature usage"
        meta={features ? `${features.total_features - features.unused_features} of ${features.total_features} features used` : undefined}>
        {!features ? <p className="usage-empty">No data yet.</p> : (
          <>
            <p className="usage-hint">
              Every tracked feature of the Safety Dashboard is listed, used or not —
              an unused one shows a zero rather than disappearing. Panels and modes
              also carry the time they were open and on screen; the rest are counts.
            </p>
            <label className="usage-inline">
              <input type="checkbox" checked={onlyUnused}
                onChange={(e) => setOnlyUnused(e.target.checked)} />
              Show only what nobody used
            </label>

            {!onlyUnused && topFeatures.length > 0 && (
              <>
                <h3>Most used</h3>
                <ResponsiveContainer width="100%" height={catChartHeight(topFeatures)}>
                  <BarChart data={topFeatures} layout="vertical"
                    margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis type="number" tick={AXIS_TICK} />
                    <YAxis type="category" dataKey="name" width={CAT_LANE}
                      tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                    <Bar dataKey="events" name="Actions" fill="#38bdf8" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {features.groups.map((g) => {
              const rows = onlyUnused ? g.features.filter((f) => !f.events) : g.features;
              if (!rows.length) return null;
              return (
                <CollapsibleGroup key={g.group} id={g.group} title={g.label}
                  meta={`${rows.length} · ${g.unused ? `${g.unused} unused` : 'all used'}${g.hours ? ` · ${fmtHours(g.hours)}` : ''}`}>
                  <div className="usage-table-wrap">
                    <table className="usage-table">
                      <thead>
                        <tr>
                          <th>Feature</th><th className="ua-num">Actions</th><th className="ua-num">Time</th>
                          <th className="ua-num">Users</th><th className="ua-num">Sessions</th><th>Last used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((f) => (
                          <tr key={f.target} className={f.events ? '' : 'ua-unused'}>
                            <td title={f.target}>{f.label}</td>
                            <td className="ua-num">{f.events || '—'}</td>
                            <td className="ua-num">{f.dwell ? fmtHours(f.hours) : '—'}</td>
                            <td className="ua-num">{f.users || '—'}</td>
                            <td className="ua-num">{f.sessions || '—'}</td>
                            <td>{fmtDateTime(f.last_seen)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CollapsibleGroup>
              );
            })}
          </>
        )}
      </SectionCard>

      {/* ---------------- Modes & data sources ---------------- */}
      <SectionCard id="ua-modes" title="Modes and data sources">
        {!modes ? <p className="usage-empty">No data yet.</p> : (
          <>
            <h3>Time per mode</h3>
            {modes.modes?.length ? (
              <ResponsiveContainer width="100%" height={catChartHeight(modes.modes)}>
                <BarChart data={modes.modes.map((m) => ({ ...m, name: m.label }))}
                  layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={AXIS_TICK} />
                  <YAxis type="category" dataKey="name" width={CAT_LANE}
                    tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Bar dataKey="hours" name="Active hours" fill="#34d399" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="usage-empty">No mode activity in this range.</p>}

            <h3>Data sources opened</h3>
            {modes.sources?.length ? (
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Source</th><th>Kind</th><th>Projects</th>
                      <th>Active hours</th><th>Users</th><th>Sessions</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modes.sources.map((s) => (
                      <tr key={s.source}>
                        <td>{s.label}</td>
                        <td>{s.kind}</td>
                        <td>
                          {s.projects?.length
                            ? s.projects.map((p) => (
                              <span key={p.id} className="ua-chip" title={`Project ${p.id}`}>{p.name}</span>
                            ))
                            : <span className="usage-muted">whole road network</span>}
                        </td>
                        <td>{fmtHours(s.hours)}</td>
                        <td>{s.users}</td>
                        <td>{s.sessions}</td>
                        <td>{s.events}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="usage-empty">No sources opened in this range.</p>}
          </>
        )}
      </SectionCard>

      {/* ---------------- Session trace ---------------- */}
      <SectionCard id="ua-trace" title="Session trace"
        meta={traceSessions ? `${sessTotal} session${sessTotal === 1 ? '' : 's'}` : undefined}
        defaultOpen={false}>
        <p className="usage-hint">
          What one person did, in the order they did it. Open a session to read
          its actions step by step, with the time between them and the filters
          that were set at each point. A gap of a minute or more is shown as a
          pause rather than as one action following the next.
        </p>
        {!traceSessions?.length ? (
          <p className="usage-empty">
            {sessLoading ? 'Loading…' : 'No Safety Dashboard sessions in this range.'}
          </p>
        ) : (
          <>
          <div className="ua-pager">
            <span className="ua-pager-count">
              {firstRow}–{lastRow} of {sessTotal}
            </span>
            <span className="ua-pager-sizes">
              {PAGE_SIZES.map((n) => (
                <button key={n} type="button"
                  className={`ua-topn${sessSize === n ? ' on' : ''}`}
                  onClick={() => setSessSize(n)}
                  title={`Show ${n} sessions per page`}>
                  {n}
                </button>
              ))}
              <span className="ua-pager-label">per page</span>
            </span>
            <span className="ua-pager-nav">
              <button type="button" className="ua-topn" disabled={sessPage === 0}
                onClick={() => setSessPage(0)} title="First page">«</button>
              <button type="button" className="ua-topn" disabled={sessPage === 0}
                onClick={() => setSessPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
              <span className="ua-pager-label">
                Page {sessPage + 1} of {pageCount}
              </span>
              <button type="button" className="ua-topn" disabled={sessPage + 1 >= pageCount}
                onClick={() => setSessPage((p) => p + 1)}>Next ›</button>
              <button type="button" className="ua-topn" disabled={sessPage + 1 >= pageCount}
                onClick={() => setSessPage(pageCount - 1)} title="Last page">»</button>
            </span>
          </div>
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th />
                  <th>User</th><th>Customer</th><th>Started</th>
                  <th className="ua-num">Length</th><th className="ua-num">Active</th>
                  <th className="ua-num">Actions</th><th>Modes</th>
                </tr>
              </thead>
              <tbody>
                {traceSessions.map((s) => {
                  const open = openTrace === s.session_id;
                  return (
                    <React.Fragment key={s.session_id}>
                      <tr className="usage-row-click" onClick={() => openSessionTrace(s.session_id)}>
                        <td>{open ? '▾' : '▸'}</td>
                        <td>{s.user_email}</td>
                        <td>{s.customer || '—'}</td>
                        <td>{fmtDateTime(s.login_time)}</td>
                        <td className="ua-num">{s.open ? <em>open</em> : fmtDuration(s.duration_s)}</td>
                        <td className="ua-num">{fmtDuration(s.active_s)}</td>
                        <td className="ua-num">{s.actions}</td>
                        <td>{s.modes?.length ? s.modes.join(', ') : '—'}</td>
                      </tr>
                      {open && (
                        <tr className="usage-row-detail">
                          <td colSpan={8}>
                            {traceLoading && <p className="usage-empty">Loading…</p>}
                            {!traceLoading && !trace?.steps?.length && (
                              <p className="usage-empty">
                                This session recorded no actions. It predates the
                                instrumented build, or the user signed in without
                                opening the Safety Dashboard.
                              </p>
                            )}
                            {!traceLoading && trace?.steps?.length > 0 && (
                              <ol className="ua-trace">
                                {trace.steps.map((st) => (
                                  <li key={st.n} className={st.paused ? 'ua-trace-paused' : ''}>
                                    <span className="ua-trace-time" title={fmtDateTime(st.ts)}>
                                      +{fmtDuration(st.offset_s)}
                                    </span>
                                    <span className={`ua-trace-kind ua-kind-${st.kind}`}>{st.kind}</span>
                                    <span className="ua-trace-what">
                                      {st.label}
                                      {st.value !== null && st.value !== undefined && (
                                        <em> → {String(st.value)}</em>
                                      )}
                                      {st.dwell_s ? <em> ({fmtDuration(st.dwell_s)})</em> : null}
                                    </span>
                                    {st.paused && (
                                      <span className="ua-trace-gap">
                                        after {fmtDuration(st.gap_s)} idle
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            )}
                            {trace?.session?.truncated && (
                              <p className="usage-hint">
                                Only the first {trace.steps.length} actions are shown.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </SectionCard>

      {/* ---------------- Filter usage ---------------- */}
      <SectionCard id="ua-filters" title="Filters they actually use" defaultOpen={false}>
        {!filterUsage ? <p className="usage-empty">No data yet.</p> : (
          <>
            <h3>Most-held filter combinations</h3>
            <p className="usage-hint">
              Each row is one distinct set of settings that was in force. Open a row
              to see the whole set exactly as it was at the time.
            </p>
            {filterUsage.combinations?.length ? (
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Settings</th><th>Actions</th><th>Active hours</th>
                      <th>Users</th><th>Sessions</th><th>Last held</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterUsage.combinations.map((c) => {
                      const open = openCombo === c.filter_hash;
                      const keys = Object.keys(c.filters || {});
                      return (
                        <React.Fragment key={c.filter_hash}>
                          <tr className="usage-row-click"
                            onClick={() => setOpenCombo(open ? null : c.filter_hash)}>
                            <td>{open ? '▾' : '▸'}</td>
                            <td>{keys.length
                              ? `${keys.length} setting${keys.length === 1 ? '' : 's'} away from empty`
                              : 'defaults'}</td>
                            <td>{c.events}</td>
                            <td>{fmtHours(c.hours)}</td>
                            <td>{c.users}</td>
                            <td>{c.sessions}</td>
                            <td>{fmtDateTime(c.last_seen)}</td>
                          </tr>
                          {open && (
                            <tr className="usage-row-detail">
                              <td colSpan={7}>
                                <dl className="ua-filter-set">
                                  {keys.length ? keys.map((k) => (
                                    <React.Fragment key={k}>
                                      <dt>{k}</dt>
                                      <dd>{typeof c.filters[k] === 'object'
                                        ? JSON.stringify(c.filters[k])
                                        : String(c.filters[k])}</dd>
                                    </React.Fragment>
                                  )) : <dd className="usage-muted">Everything at its default.</dd>}
                                </dl>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="usage-empty">No filter activity in this range.</p>}

            <h3>Controls they move</h3>
            {filterUsage.controls?.length ? (
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr><th>Control</th><th>Changes</th><th>Most-chosen values</th></tr>
                  </thead>
                  <tbody>
                    {filterUsage.controls.map((c) => (
                      <tr key={c.target}>
                        <td title={c.target}>{c.label}</td>
                        <td>{c.changes}</td>
                        <td>
                          {c.values?.length
                            ? c.values.map((v) => (
                              <span key={String(v.value)} className="ua-chip">
                                {String(v.value)} <em>×{v.n}</em>
                              </span>
                            ))
                            : <span className="usage-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="usage-empty">Nobody changed a filter in this range.</p>}
          </>
        )}
      </SectionCard>
    </>
  );
};

export default UserAnalytics;
